import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Main from '../../components/layouts/Main';
import Breadcrumb from '../../components/utils/Breadcrumb';
import PageTitle from '../../components/utils/PageTitle';
import UnauthorizedMessage from '../../components/utils/UnauthorizedMessage';
import { useUserContext } from '../../contexts/UserContext';
import { usePusher } from '../../contexts/PusherContext';
import DeploymentService from '../../services/DeploymentService';
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import formatDuration from '../../helpers/formatDuration';
import Moment from 'react-moment';
import moment from 'moment-timezone';
import ActiveDeploymentCard from '../../components/deployment/ActiveDeploymentCard';
import DeploymentDetailDrawer from '../../components/deployment/DeploymentDetailDrawer';
import {
    Table, Tag, Space, Button, Select, Popconfirm, Tooltip,
} from 'antd';
import {
    CloudServerOutlined, ReloadOutlined, DeleteFilled, EyeFilled, RedoOutlined,
    CheckCircleFilled, CloseCircleFilled, LoadingOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import styles from './DeploymentListPage.module.scss';

function StatutTag({ statut }) {
    if (statut === 'succes') return <Tag icon={<CheckCircleFilled />} color="success">Succès</Tag>;
    if (statut === 'echec') return <Tag icon={<CloseCircleFilled />} color="error">Échec</Tag>;
    if (statut === 'en_cours') return <Tag icon={<LoadingOutlined />} color="processing">En cours</Tag>;
    if (statut === 'ignore') return <Tag>Ignorée</Tag>;
    return <Tag icon={<ClockCircleOutlined />}>En attente</Tag>;
}
StatutTag.propTypes = { statut: PropTypes.string };

export default function DeploymentListPage() {
    const { currentUser } = useUserContext();
    const { pusher } = usePusher();
    const isAdmin = !!currentUser?.is_admin;

    const [deploiements, setDeploiements] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [environnements, setEnvironnements] = useState([]);
    const [ciblesASelectionner, setCiblesASelectionner] = useState([]);
    const [statutFilter, setStatutFilter] = useState(undefined);
    const [cibleFilter, setCibleFilter] = useState(undefined);
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState(null);

    const init = useCallback((targetPage = 1) => {
        setLoading(true);
        DeploymentService.getInstance()
            .search({
                page: targetPage,
                filters: { statut: statutFilter, cible: cibleFilter },
            })
            .then((res) => {
                setDeploiements(res.data ?? []);
                setMeta(res.meta ?? null);
                setPage(targetPage);
            })
            .catch(catchError)
            .finally(() => setLoading(false));
    }, [statutFilter, cibleFilter]);

    useEffect(() => {
        if (!isAdmin) return;
        init(1);
    }, [isAdmin, init]);

    useEffect(() => {
        if (!isAdmin) return;
        DeploymentService.getInstance().getEnvironnements()
            .then((res) => setEnvironnements(res.data ?? []))
            .catch(catchError);
    }, [isAdmin]);

    const trigger = useCallback((cibles) => {
        setTriggering(true);
        return DeploymentService.getInstance()
            .trigger(cibles)
            .then((res) => {
                Toast.success('Déploiement démarré.');
                setDeploiements((prev) => [res.data, ...prev]);
                setDetail(res.data); // ouvre immédiatement le suivi en direct
            })
            .catch(catchError)
            .finally(() => setTriggering(false));
    }, []);

    const handleDelete = (uid) => {
        DeploymentService.getInstance()
            .remove(uid)
            .then((res) => {
                Toast.success(res.message);
                setDeploiements((prev) => prev.filter((d) => d.uid !== uid));
            })
            .catch(catchError);
    };

    const openDetail = (uid) => {
        DeploymentService.getInstance().getDetails(uid)
            .then((res) => setDetail(res.data))
            .catch(catchError);
    };

    // Suivi en direct : met à jour la ligne du tableau ET le détail ouvert (le cas
    // échéant) étape par étape, sans jamais recharger la page.
    useEffect(() => {
        if (!pusher || !isAdmin) return;

        const channelName = 'private-deploy-supervisor';
        const channel = pusher.subscribe(channelName);

        // Insère (statut "en_cours") ou met à jour (fin d'étape) la dernière entrée
        // de `steps` portant le même label — même logique que côté backend
        // (DeploymentRunner), qui diffuse toujours l'entame AVANT le résultat.
        const patchEtapeDansResultat = (resultat, data) => {
            const next = { ...(resultat ?? {}) };
            const cible = { ...(next[data.cible] ?? { statut: 'en_cours', steps: [] }) };
            const steps = [...(cible.steps ?? [])];
            const lastIdx = steps.length - 1;
            const step = {
                label: data.label,
                statut: data.statut,
                exit_code: data.exit_code ?? null,
                duration_ms: data.duration_ms ?? null,
                output_tail: null,
            };
            if (lastIdx >= 0 && steps[lastIdx].label === data.label && steps[lastIdx].statut === 'en_cours') {
                steps[lastIdx] = step;
            } else {
                steps.push(step);
            }
            cible.steps = steps;
            next[data.cible] = cible;
            return next;
        };

        const patchCibleDansResultat = (resultat, data) => {
            const next = { ...(resultat ?? {}) };
            next[data.cible] = { ...(next[data.cible] ?? { steps: [] }), statut: data.statut };
            return next;
        };

        const applyToMatching = (uid, updater) => {
            setDeploiements((prev) => prev.map((d) => (d.uid === uid ? { ...d, resultat: updater(d.resultat) } : d)));
            setDetail((prev) => (prev && prev.uid === uid ? { ...prev, resultat: updater(prev.resultat) } : prev));
        };

        const handleEtape = (data) => applyToMatching(data.uid, (resultat) => patchEtapeDansResultat(resultat, data));
        const handleCible = (data) => applyToMatching(data.uid, (resultat) => patchCibleDansResultat(resultat, data));

        const handleTermine = (data) => {
            setDeploiements((prev) => prev.map((d) => (d.uid === data.uid ? { ...d, statut: data.statut, termine_le: data.termine_le } : d)));
            setDetail((prev) => {
                if (prev?.uid === data.uid) {
                    // La sortie console (output_tail) n'est jamais diffusée en direct —
                    // on va la chercher une fois le pipeline terminé.
                    DeploymentService.getInstance().getDetails(data.uid).then((res) => setDetail(res.data)).catch(catchError);
                    return { ...prev, statut: data.statut, termine_le: data.termine_le };
                }
                return prev;
            });
            Toast[data.statut === 'succes' ? 'success' : 'error'](`Déploiement ${data.statut === 'succes' ? 'terminé avec succès' : 'terminé en échec'}.`);
        };

        channel.bind('deploiement.etape', handleEtape);
        channel.bind('deploiement.cible', handleCible);
        channel.bind('deploiement.termine', handleTermine);

        return () => {
            channel.unbind('deploiement.etape', handleEtape);
            channel.unbind('deploiement.cible', handleCible);
            channel.unbind('deploiement.termine', handleTermine);
            pusher.unsubscribe(channelName);
        };
    }, [pusher, isAdmin]);

    const deploiementActif = useMemo(() => deploiements.find((d) => d.statut === 'en_cours'), [deploiements]);

    const stats = useMemo(() => {
        const termines = deploiements.filter((d) => d.statut !== 'en_cours' && d.demarre_le && d.termine_le);
        const succes = termines.filter((d) => d.statut === 'succes').length;
        const tauxSucces = termines.length ? Math.round((succes / termines.length) * 100) : null;
        const dureeMoyenne = termines.length
            ? termines.reduce((acc, d) => acc + moment(d.termine_le).diff(moment(d.demarre_le)), 0) / termines.length
            : null;
        return {
            dernier: deploiements[0] ?? null,
            tauxSucces,
            dureeMoyenne,
        };
    }, [deploiements]);

    if (!isAdmin) {
        return (
            <Main>
                <UnauthorizedMessage />
            </Main>
        );
    }

    const columns = [
        {
            title: 'Cibles',
            dataIndex: 'cibles',
            key: 'cibles',
            render: (cibles, record) => (
                <Space wrap>
                    {(cibles ?? []).map((code) => (
                        <Tag key={code}>
                            {environnements.find((e) => e.code === code)?.label ?? code}
                            {' '}
                            <StatutTag statut={record.resultat?.[code]?.statut} />
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Statut global',
            dataIndex: 'statut',
            key: 'statut',
            render: (statut) => <StatutTag statut={statut} />,
            width: 140,
        },
        {
            title: 'Déclenché par',
            dataIndex: 'declenche_par',
            key: 'declenche_par',
            render: (declenchePar) => declenchePar?.name ?? declenchePar?.email ?? 'N/A',
        },
        {
            title: 'Démarré',
            dataIndex: 'demarre_le',
            key: 'demarre_le',
            render: (date) => (date ? <Tooltip title={<Moment format="DD/MM/YYYY HH:mm:ss">{date}</Moment>}>{moment(date).fromNow()}</Tooltip> : 'N/A'),
            width: 150,
        },
        {
            title: 'Durée',
            key: 'duree',
            width: 110,
            render: (text, record) => {
                if (record.statut === 'en_cours') return <Tag color="processing">En cours</Tag>;
                if (!record.demarre_le || !record.termine_le) return 'N/A';
                return formatDuration(moment(record.termine_le).diff(moment(record.demarre_le)));
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (text, record) => (
                <Space>
                    <Tooltip title="Voir le détail">
                        <Button type="primary" icon={<EyeFilled />} onClick={() => openDetail(record.uid)} />
                    </Tooltip>
                    <Tooltip title="Relancer avec les mêmes cibles">
                        <Button icon={<RedoOutlined />} disabled={record.statut === 'en_cours'} onClick={() => trigger(record.cibles ?? [])} />
                    </Tooltip>
                    <Popconfirm
                        title="Supprimer ce déploiement ?"
                        disabled={record.statut === 'en_cours'}
                        okType="danger"
                        okText="Confirmer"
                        cancelText="Annuler"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button danger icon={<DeleteFilled />} disabled={record.statut === 'en_cours'} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Main>
            <Breadcrumb parentLink="/" canSee title="Déploiements" parentLinkTitle="Accueil" />
            <PageTitle title="Déploiements" subTitle="Suivi et déclenchement des déploiements backend/frontend" icon={<CloudServerOutlined />}>
                <Select
                    mode="multiple"
                    style={{ minWidth: 220 }}
                    placeholder="Cibles à déployer (toutes par défaut)"
                    value={ciblesASelectionner}
                    onChange={setCiblesASelectionner}
                    options={environnements.map((e) => ({ value: e.code, label: e.label }))}
                />
                <Popconfirm
                    title="Démarrer un déploiement ?"
                    description={ciblesASelectionner.length ? `Cibles : ${ciblesASelectionner.join(', ')}` : 'Toutes les cibles seront déployées.'}
                    okText="Démarrer"
                    cancelText="Annuler"
                    onConfirm={() => trigger(ciblesASelectionner)}
                >
                    <Button type="primary" loading={triggering}>Déployer</Button>
                </Popconfirm>
                <Button icon={<ReloadOutlined />} onClick={() => init(page)}>Rafraîchir</Button>
            </PageTitle>

            <div className={styles.statsStrip}>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Dernier déploiement</div>
                    <div className={styles.statValue}>
                        {stats.dernier ? <StatutTag statut={stats.dernier.statut} /> : '—'}
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Taux de succès (page)</div>
                    <div className={styles.statValue}>{stats.tauxSucces != null ? `${stats.tauxSucces}%` : '—'}</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statLabel}>Durée moyenne (page)</div>
                    <div className={styles.statValue}>{stats.dureeMoyenne != null ? formatDuration(stats.dureeMoyenne) : '—'}</div>
                </div>
            </div>

            <ActiveDeploymentCard
                deploiement={deploiementActif}
                environnements={environnements}
                onViewDetails={openDetail}
            />

            <Space style={{ marginBottom: 16 }}>
                <Select
                    allowClear
                    placeholder="Filtrer par statut"
                    style={{ width: 180 }}
                    value={statutFilter}
                    onChange={setStatutFilter}
                    options={[
                        { value: 'en_cours', label: 'En cours' },
                        { value: 'succes', label: 'Succès' },
                        { value: 'echec', label: 'Échec' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="Filtrer par cible"
                    style={{ width: 180 }}
                    value={cibleFilter}
                    onChange={setCibleFilter}
                    options={environnements.map((e) => ({ value: e.code, label: e.label }))}
                />
            </Space>

            <Table
                loading={loading}
                rowKey="uid"
                columns={columns}
                dataSource={deploiements}
                pagination={{
                    current: meta?.current_page ?? 1,
                    pageSize: meta?.per_page ?? 20,
                    total: meta?.total ?? 0,
                    onChange: (targetPage) => init(targetPage),
                }}
            />

            <DeploymentDetailDrawer
                deploiement={detail}
                environnements={environnements}
                onClose={() => setDetail(null)}
            />
        </Main>
    );
}
